import { Box, Paper } from "@newulife/common";
import { useContentfulContext } from "hooks/useContentful";
import Markdown from "markdown-to-jsx";
import "./styles.css";

export default function LoyaltyProgress({
  currentRank = 'bronze',
  earnedMonths = 0,
  missedMonths = 0,
}) {
  const data = useContentfulContext({ entryPoint: "fields.banner8Ref.fields" });
  const {
    title,
    text1: consecutiveMonths,
    text2: currentTier,
    text3: nextTier,
    text4: freeMonthsRemaining,
    text5: bronze,
    text6: silver,
    text7: gold,
    text8: platinum,
    text9: ofText,
    inputModelArray: rankBenefits,
    buttonText1: primaryCtaText,
    buttonText2: primaryCtaLink,
    buttonText3: viewProgramDetails,
    buttonText4: viewProgramDetailsLink,
  } = data;

  const {
    item2: bronzeBenefits,
    item3: silverBenefits,
    item4: goldBenefits,
    item5: platinumBenefits,
  } = rankBenefits?.[0]?.fields;

  const devRankHandling = currentRank === "VIP" ? "bronze" : currentRank?.toLowerCase();
  const standardRank = devRankHandling.includes(" vip") ? devRankHandling.replace(" vip", "") : devRankHandling;

  const TIERS = {
    bronze: {
      name: bronze,
      benefits: bronzeBenefits,
      nextRank: silver,
    },
    silver: {
      name: silver,
      benefits: silverBenefits,
      nextRank: gold,
    },
    gold: {
      name: gold,
      benefits: goldBenefits,
      nextRank: platinum,
    },
    platinum: {
      name: platinum,
      benefits: platinumBenefits,
    },
  };

  const currentTierName = TIERS[standardRank]?.name;
  const currentTierBenefits = TIERS[standardRank]?.benefits;
  const nextTierName = TIERS[TIERS[standardRank].nextRank?.toLowerCase()]?.name;
  const nextTierBenefits = TIERS[TIERS[standardRank].nextRank?.toLowerCase()]?.benefits;

  return (
    <Paper
      sx={{
        width: "100%",
        maxWidth: 415,
      }}
      elevation={0}
    >
      <Box
        sx={{
          color: "#007EC3",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 1.5,
          fontWeight: 700,
          WebkitTapHighlightColor: "transparent",
        }}
      >
        <h1 style={{ fontSize: "1.5rem", textAlign: "center" }}>{title}</h1>
      </Box>

      <div className={`progress-container month-${earnedMonths}`}>
      <div className="logo">
            <svg width="84" height="32" viewBox="0 0 84 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0_123_26)">
                    <mask id="mask0_123_26" maskUnits="userSpaceOnUse" x="0" y="0" width="84" height="32">
                        <path d="M83.9273 0H0V31.9127H83.9273V0Z" fill="white" />
                    </mask>
                    <g mask="url(#mask0_123_26)">
                        <path d="M34.4508 5.6289V10.8398C34.4508 12.4398 33.5963 13.098 32.4745 13.098C31.3526 13.098 30.4981 12.4216 30.4981 10.8398V3.35254C29.2399 3.35254 28.2199 4.37254 28.2199 5.6289V10.8398C28.2199 12.4216 27.3654 13.098 26.2436 13.098C25.1217 13.098 24.2672 12.4398 24.2672 10.8398V5.6289C24.2672 4.37254 23.2472 3.35254 21.989 3.35254V10.8398C21.989 13.8271 23.7508 15.2144 26.0472 15.2144C27.5254 15.2144 28.6817 14.5925 29.3581 13.4362C30.0345 14.5925 31.1908 15.2144 32.669 15.2144C34.9654 15.2144 36.7272 13.8271 36.7272 10.8398V3.35254C35.469 3.35254 34.449 4.37254 34.449 5.6289H34.4472H34.4508Z" fill="#043653" />
                        <path d="M18.3018 5.4689C19.4708 5.4689 20.4199 4.52163 20.4199 3.35254H11.8381V14.9834H20.4199C20.4199 13.8234 19.4799 12.8853 18.3199 12.8853H14.1181V10.0925H17.3581C18.4781 10.0925 19.3872 9.18527 19.3872 8.06527H14.1181V5.4689H18.3018Z" fill="#043653" />
                        <path d="M4.9492 3.12012C3.58557 3.12012 2.48557 3.46375 1.6692 4.08921C1.25284 3.6383 0.661927 3.35103 -0.00170898 3.35103V14.9819C1.25647 14.9819 2.27829 13.9619 2.27829 12.7056V7.97466C2.27829 6.24921 3.1692 5.23466 4.9492 5.23466C6.7292 5.23466 7.61829 6.24921 7.61829 7.97466V12.7056C7.61829 13.9619 8.63829 14.9819 9.89829 14.9819V7.97466C9.89829 4.98739 8.13466 3.12012 4.9492 3.12012Z" fill="#043653" />
                        <path d="M53.6909 0C50.9472 0 48.7236 2.22182 48.7236 4.96182L48.7436 8.38364V8.93455C48.7436 11.3582 46.7781 13.3218 44.3527 13.3218C43.5181 13.3218 42.7399 13.0909 42.0763 12.6873C42.0709 12.6855 42.0672 12.6836 42.0618 12.6818C43.2763 14.0982 45.0763 15 47.0909 15C50.749 15 53.7127 12.0382 53.7127 8.38545L53.6927 0.00181818L53.6909 0Z" fill="#043653" />
                        <path d="M43.5165 12.7238C43.5583 12.7329 43.6019 12.7401 43.6438 12.7492C43.6619 12.7529 43.6783 12.7547 43.6965 12.7583C43.7347 12.7638 43.7728 12.7711 43.8128 12.7765C43.8365 12.7801 43.8583 12.782 43.8819 12.7856C43.9165 12.7892 43.9528 12.7929 43.9874 12.7965C44.0165 12.8001 44.0474 12.802 44.0765 12.8038C44.1074 12.8056 44.1365 12.8074 44.1674 12.8092C44.2201 12.8111 44.2728 12.8129 44.3238 12.8129C44.331 12.8129 44.3401 12.8129 44.3474 12.8129H44.351C44.4128 12.8129 44.4747 12.8129 44.5347 12.8074C44.5419 12.8074 44.5474 12.8074 44.5547 12.8074C45.7692 12.7438 46.8365 12.1238 47.5038 11.1983C47.5492 11.1311 47.5965 11.0638 47.6419 10.9929C47.6419 10.9929 47.6383 10.9929 47.6365 10.9929C47.591 11.002 47.3183 11.0929 47.1001 11.102C46.1219 11.1474 45.431 10.0165 45.431 8.81469L45.4456 5.21651V4.77651C45.4456 4.74378 45.4456 4.71287 45.4419 4.68196C45.4328 4.5256 45.4165 4.37287 45.3947 4.22014C45.2874 3.50196 45.0238 2.83469 44.6419 2.25105C43.7565 0.896499 42.2256 0.00195312 40.4856 0.00195312L40.471 6.8656L40.4674 8.21469C40.4674 8.27469 40.4656 8.33469 40.4656 8.39469C40.4656 8.44378 40.4656 8.49105 40.4656 8.54014V8.89105C40.4656 8.89105 40.4656 8.89651 40.4656 8.89832C40.4656 8.90923 40.4656 8.92014 40.4656 8.93105V8.93469C40.4656 8.94741 40.4656 8.95832 40.4656 8.97105L40.4747 9.21105C40.4783 9.25469 40.4819 9.29651 40.4874 9.34014C40.4874 9.3456 40.4874 9.35105 40.4874 9.35651C40.4928 9.39832 40.4983 9.44014 40.5038 9.48196C40.5038 9.49287 40.5074 9.50196 40.5092 9.51287C40.5147 9.55469 40.5219 9.59651 40.5292 9.63832C40.5292 9.64378 40.5292 9.64741 40.5328 9.65287C40.5965 9.99469 40.7038 10.3201 40.851 10.6238C41.3583 11.6711 42.3238 12.4529 43.4874 12.7165C43.4965 12.7183 43.5056 12.7201 43.5147 12.722L43.5165 12.7238Z" fill="#043653" />
                        <path d="M1.30909 31.5856H0V18.7129H3.78182C4.41273 18.7129 4.99636 18.7729 5.53636 18.8947C6.07636 19.0165 6.54182 19.2129 6.93636 19.4856C7.33091 19.7583 7.63636 20.1129 7.85455 20.5493C8.07273 20.9856 8.18182 21.5183 8.18182 22.1493C8.18182 22.622 8.09636 23.0529 7.92727 23.4402C7.75818 23.8274 7.52727 24.1638 7.23636 24.4493C6.94545 24.7347 6.6 24.962 6.2 25.1311C5.8 25.3002 5.36364 25.4093 4.89091 25.4583L8.65454 31.5856H7.05454L3.49091 25.5674H1.30909V31.5856ZM1.30909 24.4402H3.58182C4.63636 24.4402 5.44909 24.2493 6.01818 23.8674C6.58727 23.4856 6.87273 22.9129 6.87273 22.1493C6.87273 21.7256 6.79455 21.3674 6.63636 21.0765C6.47818 20.7856 6.26 20.5493 5.98182 20.3674C5.70364 20.1856 5.36364 20.0529 4.96364 19.9674C4.56364 19.8838 4.12182 19.8402 3.63636 19.8402H1.30909V24.4402Z" fill="#010101" />
                        <path d="M12.2 30.422H19.0909V31.5856H10.8909V18.7129H18.8727V19.8765H12.2V24.2947H18.4363V25.4583H12.2V30.422Z" fill="#010101" />
                        <path d="M24.7636 29.7856H24.8L28.0181 18.7129H29.5818L32.8 29.7856H32.8363L35.9818 18.7129H37.3636L33.5636 31.5856H32.0363L28.8 20.5311H28.7636L25.5272 31.5856H24L20.2 18.7129H21.5818L24.7636 29.7856Z" fill="#010101" />
                        <path d="M39.0182 31.5856H37.5818L43.1454 18.7129H44.4182L49.9454 31.5856H48.4909L47.0727 28.2038H40.4182L39.0182 31.5856ZM40.9091 27.0038H46.5818L43.7454 20.1674L40.9091 27.0038Z" fill="#010101" />
                        <path d="M53.1455 31.5856H51.8364V18.7129H55.6182C56.2492 18.7129 56.8328 18.7729 57.3728 18.8947C57.9128 19.0165 58.3782 19.2129 58.7728 19.4856C59.1673 19.7583 59.4728 20.1129 59.691 20.5493C59.9091 20.9856 60.0182 21.5183 60.0182 22.1493C60.0182 22.622 59.9328 23.0529 59.7637 23.4402C59.5946 23.8274 59.3637 24.1638 59.0728 24.4493C58.7819 24.7347 58.4364 24.962 58.0364 25.1311C57.6364 25.3002 57.2001 25.4093 56.7273 25.4583L60.491 31.5856H58.891L55.3273 25.5674H53.1455V31.5856ZM53.1455 24.4402H55.4182C56.4728 24.4402 57.2855 24.2493 57.8546 23.8674C58.4237 23.4856 58.7092 22.9129 58.7092 22.1493C58.7092 21.7256 58.631 21.3674 58.4728 21.0765C58.3146 20.7856 58.0964 20.5493 57.8182 20.3674C57.5401 20.1856 57.2001 20.0529 56.8001 19.9674C56.4001 19.8838 55.9582 19.8402 55.4728 19.8402H53.1455V24.4402Z" fill="#010101" />
                        <path d="M62.7273 18.7129H67C67.8 18.7129 68.6037 18.8456 69.4091 19.1129C70.2146 19.3802 70.9418 19.782 71.5909 20.322C72.24 20.862 72.7637 21.5347 73.1637 22.3402C73.5637 23.1456 73.7637 24.082 73.7637 25.1493C73.7637 26.2165 73.5637 27.1674 73.1637 27.9674C72.7637 28.7674 72.24 29.4365 71.5909 29.9765C70.9418 30.5165 70.2146 30.9183 69.4091 31.1856C68.6037 31.4529 67.8 31.5856 67 31.5856H62.7273V18.7129ZM64.0364 30.422H66.6364C67.6055 30.422 68.4509 30.2711 69.1728 29.9674C69.8946 29.6638 70.4909 29.2674 70.9637 28.7765C71.4364 28.2856 71.7909 27.7256 72.0273 27.0947C72.2637 26.4638 72.3818 25.8165 72.3818 25.1493C72.3818 24.482 72.2637 23.8347 72.0273 23.2038C71.7909 22.5729 71.4364 22.0129 70.9637 21.522C70.4909 21.0311 69.8946 20.6347 69.1728 20.3311C68.4509 20.0274 67.6055 19.8765 66.6364 19.8765H64.0364V30.422Z" fill="#010101" />
                        <path d="M82.7273 20.7857C82.4491 20.3985 82.0909 20.0857 81.6546 19.8494C81.2182 19.613 80.6909 19.4948 80.0727 19.4948C79.7582 19.4948 79.4364 19.5439 79.1091 19.6403C78.7818 19.7367 78.4855 19.8857 78.2182 20.0857C77.9509 20.2857 77.7364 20.5367 77.5727 20.8403C77.4091 21.1439 77.3273 21.5003 77.3273 21.913C77.3273 22.3257 77.4055 22.6639 77.5618 22.9312C77.7182 23.1985 77.9273 23.4257 78.1873 23.613C78.4473 23.8003 78.7436 23.9548 79.0764 24.0767C79.4091 24.1985 79.7564 24.3185 80.1182 24.4403C80.5655 24.5857 81.0146 24.7439 81.4691 24.913C81.9218 25.0821 82.3309 25.3039 82.6927 25.5767C83.0546 25.8494 83.3509 26.1948 83.5818 26.613C83.8109 27.0312 83.9273 27.5621 83.9273 28.2039C83.9273 28.8457 83.8055 29.4003 83.56 29.8676C83.3146 30.3348 82.9964 30.7185 82.6055 31.0221C82.2146 31.3257 81.7673 31.5494 81.2636 31.6948C80.76 31.8403 80.2546 31.913 79.7455 31.913C79.3582 31.913 78.9691 31.873 78.5818 31.7948C78.1946 31.7167 77.8236 31.5985 77.4727 31.4403C77.1218 31.2821 76.7946 31.0821 76.4909 30.8403C76.1873 30.5985 75.9218 30.3185 75.6909 30.0039L76.8182 29.1676C77.0964 29.6276 77.4873 30.013 77.9909 30.3221C78.4946 30.6312 79.0855 30.7857 79.7636 30.7857C80.0909 30.7857 80.4236 30.7348 80.7636 30.6312C81.1036 30.5276 81.4091 30.3712 81.6818 30.1585C81.9546 29.9457 82.1782 29.6857 82.3546 29.3767C82.5291 29.0676 82.6182 28.7076 82.6182 28.2948C82.6182 27.8457 82.5309 27.473 82.3546 27.1767C82.1782 26.8803 81.9455 26.6312 81.6546 26.4312C81.3636 26.2312 81.0309 26.0639 80.6546 25.9312C80.2782 25.7985 79.8855 25.6639 79.4727 25.5312C79.0491 25.3985 78.6309 25.2457 78.2182 25.0767C77.8055 24.9076 77.4364 24.6857 77.1091 24.413C76.7818 24.1403 76.5182 23.8039 76.3182 23.4039C76.1182 23.0039 76.0182 22.513 76.0182 21.9312C76.0182 21.313 76.1418 20.7803 76.3909 20.3312C76.64 19.8839 76.9636 19.513 77.3636 19.2221C77.7636 18.9312 78.2055 18.7185 78.6909 18.5857C79.1764 18.453 79.6546 18.3857 80.1273 18.3857C81 18.3857 81.7455 18.5403 82.3636 18.8494C82.9818 19.1585 83.4491 19.5257 83.7636 19.9494L82.7273 20.7857Z" fill="#010101" />
                    </g>
                </g>
                <defs>
                    <clipPath id="clip0_123_26">
                        <rect width="84" height="32" fill="white" />
                    </clipPath>
                </defs>
            </svg>
        </div>
        <div>
          <div className="chart-container">
            <div className="progress-line"></div>
            <div className="progress-bar-bkg"></div>
            <div className="progress-bar"></div>

            <div className="progress-text">
              <div className="number">{earnedMonths}</div>
              <div className="label">{consecutiveMonths}</div>
            </div>
            <div className="lock-circle">
              <svg
                width="9"
                height="12"
                viewBox="0 0 9 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_1360_26)">
                  <path
                    d="M7.63 5.01V3.34C7.63 1.5 6.13 0 4.29 0C2.45 0 0.94 1.5 0.94 3.34V5C0.35 5.73 0 6.67 0 7.68C0 10.05 1.92 11.96 4.28 11.96C6.65 11.96 8.56 10.04 8.56 7.68C8.56 6.67 8.21 5.74 7.62 5L7.63 5.01ZM4.28 1.25C5.43 1.25 6.37 2.19 6.37 3.34V3.95C5.75 3.6 5.04 3.4 4.28 3.4C3.52 3.4 2.81 3.6 2.19 3.95V3.34C2.19 2.19 3.13 1.25 4.28 1.25ZM5.16 9.38H3.4L3.69 7.53C3.48 7.34 3.36 7.06 3.4 6.74C3.46 6.35 3.78 6.04 4.17 6C4.7 5.94 5.15 6.36 5.15 6.87C5.15 7.13 5.04 7.36 4.86 7.53L5.15 9.38H5.16Z"
                    fill="white"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_1360_26">
                    <rect width="8.57" height="11.97" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </div>
          </div>

          {/* As of right now, the business requirement allows 2 missed months */}
          {(missedMonths > 0 && missedMonths <= 2 )&& (
            <div className="missed-months-alert">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="20px"
                viewBox="0 -960 960 960"
                width="20px"
                fill="#993b26"
              >
                <path d="M139.93-164q-10.08 0-17.4-4.42-7.31-4.42-11.37-11.66-4.68-7.08-4.15-16 .53-8.92 5.08-17.16l340.43-565.52q4.56-8.24 11.93-11.66t15.58-3.42q8.2 0 15.55 3.42 7.34 3.42 11.9 11.66l340.43 565.52q4.55 8.24 5.08 17.16.53 8.92-4.15 16-4.49 7.14-11.59 11.61T820.07-164H139.93ZM175-216h610L480-724 175-216Zm304.79-55.69q11.94 0 20.23-8.08 8.29-8.08 8.29-20.02t-8.08-20.23q-8.08-8.29-20.02-8.29t-20.23 8.08q-8.29 8.08-8.29 20.02t8.08 20.23q8.08 8.29 20.02 8.29Zm.01-104.62q10.97 0 18.58-7.47 7.62-7.48 7.62-18.53v-140q0-11.05-7.42-18.52-7.42-7.48-18.38-7.48-10.97 0-18.58 7.48-7.62 7.47-7.62 18.52v140q0 11.05 7.42 18.53 7.42 7.47 18.38 7.47ZM480-470Z" />
              </svg>
              <p>
                {missedMonths} {ofText} 2 {freeMonthsRemaining}
              </p>
            </div>
          )}
        </div>

        <div className="rank-to-rank">
          <div className="rank-box current-rank">
            <div className="rank-title">
              <div className="current-rank-color-circle"></div>
              <div className="rank-text">
                <div className="rank-label">{currentTier}</div>
                <div className="rank-name">{currentTierName}</div>
              </div>
            </div>
            <Markdown
              options={{
                overrides: {
                  ul: {
                    props: {
                      className: "rank-benefits",
                    },
                  },
                },
              }}
            >
              {currentTierBenefits}
            </Markdown>
          </div>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0_1360_33)">
              <path
                d="M8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16Z"
                fill="#CDCFD1"
              />
              <path
                d="M6.46001 6.99995H4.26001V7.99995V8.99995H6.46001V12.1799L12.64 7.99995L6.46001 3.81995V6.99995Z"
                fill="white"
              />
            </g>
            <defs>
              <clipPath id="clip0_1360_33">
                <rect width="16" height="16" fill="white" />
              </clipPath>
            </defs>
          </svg>

          <div className="rank-box next-rank">
            <div className="rank-title">
              <div className="next-rank-color-circle"></div>
              <div className="rank-text">
                <div className="rank-label">{nextTier}</div>
                <div className="rank-name">{nextTierName}</div>
              </div>
            </div>
            <Markdown
              options={{
                overrides: {
                  ul: {
                    props: {
                      className: "rank-benefits",
                    },
                  },
                },
              }}
            >
              {nextTierBenefits}
            </Markdown>
          </div>
        </div>

        <Box className="cta">
          {primaryCtaText && (
            <a className="cta-button" href={primaryCtaLink || "#"}>{primaryCtaText}</a>
          )}
          <a href={viewProgramDetailsLink}>
            {viewProgramDetails}
          </a>
        </Box>
      </div>
    </Paper>
  );
}
